import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { useRenderJobsStore, type RenderJob } from '@/stores/renderJobs'
import { useRenderJobPolling } from '@/hooks/useRenderJobPolling'
import { useRehydrateRenderJobs } from '@/hooks/useRehydrateRenderJobs'
import { CANCEL_DIRECTOR_JOB } from '@/graphql/editTemplate'
import { ProgressBar } from '@/components/ProgressBar'
import { Button } from '@/components/ui'
import { IconChevronDown, IconChevronUp, IconX, IconCircleCheck, IconAlertTriangle } from '@/components/icons'
import { formatRelativeOrCalendarDate } from '@/lib/utils'

const STAGE_LABELS: Record<string, string> = {
  queued: 'Queued',
  processing: 'Processing',
  resolving: 'Preparing',
  rendering: 'Rendering',
  bundling: 'Building bundle',
  uploading: 'Uploading',
}

function JobRow({
  job,
  onDismiss,
  onView,
  onCancel,
}: {
  job: RenderJob
  onDismiss: () => void
  onView: () => void
  onCancel: () => void
}) {
  const isComplete = job.status === 'completed'
  const isFailed = job.status === 'failed'
  const isCancelled = job.status === 'cancelled'
  const isTerminal = isComplete || isFailed || isCancelled
  const isExport = job.kind === 'export'

  return (
    <div className="border-b border-gray-100 px-4 py-3 last:border-0">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900">{job.projectName}</p>
          <p className="text-[11px] text-gray-400">
            {isExport ? 'Premiere export' : 'Video render'}
            {job.createdAt ? ` · created ${formatRelativeOrCalendarDate(job.createdAt)}` : ''}
          </p>
        </div>
        {isComplete ? (
          <IconCircleCheck className="h-4 w-4 flex-shrink-0 text-green-600" />
        ) : isFailed ? (
          <IconAlertTriangle className="h-4 w-4 flex-shrink-0 text-red-600" />
        ) : null}
      </div>

      {isFailed && <p className="mb-2 text-xs text-red-600">{job.error || 'Render failed'}</p>}
      {isCancelled && <p className="mb-2 text-xs text-gray-500">Cancelled</p>}
      {!isTerminal && (
        <>
          <ProgressBar value={job.percent} className="mb-1" active />
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs text-gray-500">
              {STAGE_LABELS[job.stage ?? ''] ?? 'In progress'} · {job.percent}%
            </p>
            <button
              onClick={onCancel}
              className="flex-shrink-0 text-[11px] font-medium text-gray-400 hover:text-red-600"
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {isTerminal && (
        <div className="flex items-center gap-2">
          {isComplete &&
            (isExport ? (
              // An export is a .zip, not something to "view" — offer the download directly.
              job.downloadUrl ? (
                <Button size="sm" onClick={() => window.open(job.downloadUrl, '_blank', 'noopener')}>
                  Download
                </Button>
              ) : null
            ) : (
              <Button size="sm" onClick={onView}>
                View
              </Button>
            ))}
          <Button size="sm" variant="secondary" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      )}
    </div>
  )
}

// Floating bottom-center tracker for in-flight branded-reel renders, mounted once in Layout.tsx
// so it survives navigating away from the Submitted tab while a render is still running —
// modeled after Google Drive's upload-progress panel. Hidden entirely when there are no jobs.
export function RenderProgressWidget() {
  // Re-seed the store with the team's still-running jobs on load (survives a full reload), then
  // poll whatever's in the store until each job reaches a terminal status.
  useRehydrateRenderJobs()
  useRenderJobPolling()
  const navigate = useNavigate()
  const jobs = useRenderJobsStore((s) => s.jobs)
  const removeJob = useRenderJobsStore((s) => s.removeJob)
  const updateJob = useRenderJobsStore((s) => s.updateJob)
  const [cancelDirectorJob] = useMutation(CANCEL_DIRECTOR_JOB)
  const [collapsed, setCollapsed] = useState(false)

  // Cancel server-side first; only flip the row to cancelled once that lands, so a failed request
  // leaves the job polling (rather than stranding a still-running job as "cancelled" in the UI).
  const handleCancel = async (id: string) => {
    try {
      await cancelDirectorJob({ variables: { id } })
      updateJob(id, { status: 'cancelled', stage: 'cancelled' })
    } catch {
      // Leave the job as-is; it keeps polling and the user can retry Cancel.
    }
  }

  if (jobs.length === 0) return null

  const inFlight = jobs.filter(
    (j) => j.status !== 'completed' && j.status !== 'failed' && j.status !== 'cancelled',
  )
  const inFlightCount = inFlight.length
  const exportCount = inFlight.filter((j) => j.kind === 'export').length
  const renderCount = inFlightCount - exportCount
  const headerParts = [
    renderCount > 0 ? `Rendering ${renderCount} video${renderCount === 1 ? '' : 's'}` : null,
    exportCount > 0 ? `Exporting ${exportCount}` : null,
  ].filter(Boolean)
  const headerLabel = inFlightCount > 0 ? headerParts.join(' · ') : 'Renders & exports'

  return (
    <div className="fixed bottom-4 left-1/2 z-40 w-80 -translate-x-1/2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2.5">
        <p className="text-sm font-medium text-gray-900">{headerLabel}</p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
          >
            {collapsed ? <IconChevronUp className="h-4 w-4" /> : <IconChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={() => jobs.forEach((j) => removeJob(j.id))}
            className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
          >
            <IconX className="h-4 w-4" />
          </button>
        </div>
      </div>
      {!collapsed && (
        <div className="max-h-80 overflow-y-auto">
          {jobs.map((job) => (
            <JobRow
              key={job.id}
              job={job}
              onDismiss={() => removeJob(job.id)}
              onView={() => navigate('/edited-content')}
              onCancel={() => handleCancel(job.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
